import { TimeRangePickerProps } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'

// Already part of dayjs (no new dependency); gives startOf/endOf('quarter').
dayjs.extend(quarterOfYear)

type Preset = NonNullable<TimeRangePickerProps['presets']>[number]

const range = (start: Dayjs, end: Dayjs): [Dayjs, Dayjs] => [
  start.startOf('day'),
  end.endOf('day'),
]

/**
 * Presets of the backward-looking membership date pickers (Revenue "Over the
 * period", Analytics, Transactions). Built on call, not at module load, so a
 * page left open past midnight does not keep yesterday's "today".
 * The first one ("Last 30 days") is the default selection of Analytics and
 * Transactions; Revenue defaults to "Last 12 months" (`lastTwelveMonthsRange`).
 */
const analyticsRangePresets = (): Preset[] => {
  const today = dayjs()
  const yesterday = today.subtract(1, 'day')
  const lastQuarter = today.subtract(1, 'quarter')
  const lastYear = today.subtract(1, 'year')

  return [
    {
      label: 'Last 30 days',
      value: range(today.subtract(30, 'day'), yesterday),
    },
    {
      label: 'Last 90 days',
      value: range(today.subtract(90, 'day'), yesterday),
    },
    { label: 'This month', value: range(today.startOf('month'), today) },
    {
      label: 'Last month',
      value: range(
        today.subtract(1, 'month').startOf('month'),
        today.subtract(1, 'month').endOf('month')
      ),
    },
    { label: 'This quarter', value: range(today.startOf('quarter'), today) },
    {
      label: 'Last quarter',
      value: range(
        lastQuarter.startOf('quarter'),
        lastQuarter.endOf('quarter')
      ),
    },
    { label: 'This year', value: range(today.startOf('year'), today) },
    {
      label: 'Last year',
      value: range(lastYear.startOf('year'), lastYear.endOf('year')),
    },
    {
      label: 'Last 12 months',
      value: range(today.subtract(365, 'day'), yesterday),
    },
  ]
}

/**
 * Presets of the Renewals picker, which also looks ahead. "Last month" and
 * "Last year" end before today, so they are the ones that carry a renewal rate
 * and churn. All stay far under the API's 3-year limit.
 */
const renewalRangePresets = (): Preset[] => {
  const today = dayjs()
  const lastYear = today.subtract(1, 'year')

  return [
    { label: 'Next 30 days', value: range(today, today.add(30, 'day')) },
    { label: 'Next 90 days', value: range(today, today.add(90, 'day')) },
    {
      label: 'This month',
      value: range(today.startOf('month'), today.endOf('month')),
    },
    {
      label: 'Last month',
      value: range(
        today.subtract(1, 'month').startOf('month'),
        today.subtract(1, 'month').endOf('month')
      ),
    },
    {
      label: 'This year',
      value: range(today.startOf('year'), today.endOf('year')),
    },
    {
      label: 'Last year',
      value: range(lastYear.startOf('year'), lastYear.endOf('year')),
    },
  ]
}

/** Default selection of the Analytics and Transactions pickers: "Last 30 days". */
const defaultAnalyticsRange = (): [Dayjs, Dayjs] =>
  analyticsRangePresets()[0].value as [Dayjs, Dayjs]

/**
 * Default of the Revenue page: "Last 12 months", so the monthly chart always
 * shows a full year of values, whatever the current month.
 */
const lastTwelveMonthsRange = (): [Dayjs, Dayjs] =>
  analyticsRangePresets().find((preset) => preset.label === 'Last 12 months')!
    .value as [Dayjs, Dayjs]

export {
  analyticsRangePresets,
  defaultAnalyticsRange,
  renewalRangePresets,
  lastTwelveMonthsRange,
}
